import { apiClient } from './client'
import type { Indicator, Evidence } from './goals'

// Question Board (task #15) — the discovery leg beside goals/actionables.
// A question anchors a baseline AT ASK TIME and resolves on its own data clock (BB+ROPE).

export type QuestionStatus = 'open' | 'gathering' | 'answered' | 'killed'
export type QuestionVerdict = 'moved_up' | 'moved_down' | 'no_meaningful_change' | 'manual'

export interface QuestionPreview {
  state: 'open' | 'gathering' | 'answerable' | 'informational' | QuestionStatus
  verdict?: QuestionVerdict | null
  age_days: number
  current?: number | null
  baseline?: number | null
  delta_pct?: number | null
  p_better?: number
  p_worse?: number
  /** P(the metric is INSIDE the ROPE) — high = confidently "no meaningful change" (answered-null). */
  p_inside?: number
  threshold?: number
  rope?: number
}

export interface Question {
  question_id: string
  store_id: string
  question: string
  notes: string
  status: QuestionStatus
  indicator: Indicator | null
  risk_level: 'safe' | 'balanced' | 'agile'
  rope_sensitivity: 'low' | 'default' | 'high'
  answer: (Partial<QuestionPreview> & { verdict: QuestionVerdict; summary?: string; decision?: string | null; answered_at: string }) | null
  /** propose_harvest (MCP): a pending answer proposal awaiting the consultant's
   *  Responder gate. Board columns ignore it; confirming merges decision + clears. */
  pending_harvest?: { respuesta: string; decision?: string | null; proposed_at: string; proposed_by?: string | null; source: 'mcp' } | null
  links: { goal_id: string | null; actionable_id: string | null }
  evidence?: Evidence | null
  source: 'consultant' | 'ai_assisted'
  created_at: string
  updated_at: string
  preview?: QuestionPreview // populated by the read path
}

class QuestionService {
  async list(storeId: string): Promise<Question[]> {
    const res = await apiClient.get<{ questions: Question[] }>(`/api/questions?store_id=${storeId}`)
    return res.questions || []
  }

  async create(data: {
    store_id: string
    question: string
    notes?: string
    indicator?: Indicator
    risk_level?: Question['risk_level']
    rope_sensitivity?: Question['rope_sensitivity']
    links?: Partial<Question['links']>
    evidence?: Partial<Evidence>
  }): Promise<Question> {
    const res = await apiClient.post<{ question: Question }>('/api/questions', data)
    return res.question
  }

  async update(questionId: string, data: Partial<Question> & { answer?: { verdict?: QuestionVerdict; summary?: string } }): Promise<Question> {
    const res = await apiClient.put<{ question: Question }>(`/api/questions/${questionId}`, data)
    return res.question
  }

  /** Harvest: freeze the current verdict as the answer (server computes it at harvest
   * time; a pending proposal's decision is consumed unless an explicit one is given). */
  async harvest(questionId: string, summary?: string, decision?: string): Promise<Question> {
    const answer = (summary || decision) ? { ...(summary ? { summary } : {}), ...(decision ? { decision } : {}) } : undefined
    return this.update(questionId, { status: 'answered', answer } as any)
  }

  async kill(questionId: string): Promise<void> {
    await apiClient.delete(`/api/questions/${questionId}`)
  }
}

export const questionService = new QuestionService()
